<template>
  <div ref="rootRef" class="bitts-filter-multiselect">
    <BittsPopover
      ref="popoverRef"
      placement="bottomLeft"
      v-model:open="isDropdownOpen"
      :destroy-on-close="true"
      :mount-to-body="mountToBody"
      :overlay-class="popoverClasses"
      :overlay-style="overlayStyle"
    >
      <!-- eslint-disable-next-line vuejs-accessibility/interactive-supports-focus -- tabindex is bound -->
      <div
        role="combobox"
        :aria-expanded="isDropdownOpen"
        aria-haspopup="listbox"
        :aria-disabled="disabled"
        :aria-invalid="error"
        :aria-label="ariaLabel"
        :tabindex="disabled ? -1 : 0"
        class="bitts-filter-multiselect__trigger"
        :class="{
          open: isDropdownOpen,
          disabled,
          error,
        }"
        @click="handleTriggerClick"
        @keydown.enter="handleTriggerClick"
        @keydown.space.prevent="handleTriggerClick"
      >
        <div
          v-if="selectedItems.length === 0"
          class="bitts-filter-multiselect__placeholder"
        >
          {{ placeholder }}
        </div>

        <div
          v-else
          ref="selectedItemsContainer"
          class="bitts-filter-multiselect__selected-items"
          :class="{
            'bitts-filter-multiselect__selected-items--overflow': useOverflow,
          }"
        >
          <template
            v-for="(item, index) in selectedItems"
            :key="getItemKey(item)"
          >
            <slot
              name="selected-item"
              :item="item"
              :on-remove="() => handleRemove(item)"
            >
              <BittsTag
                :ref="(el) => setTagRef(el, index)"
                size="x-small"
                color="neutral"
                class="bitts-filter-multiselect__tag"
                :class="{
                  'bitts-filter-multiselect__tag--hidden':
                    useOverflow && index >= visibleCount,
                }"
              >
                <div class="flex items-center gap-4">
                  <slot name="tagPrefix" :item="item"></slot>
                  <span>{{ item.label }}</span>
                  <button
                    type="button"
                    class="bitts-filter-multiselect__remove-button"
                    @click.stop="handleRemove(item)"
                    :aria-label="`Remove ${item.label}`"
                  >
                    <FontAwesomeIcon
                      :icon="['fak', 'x']"
                      :style="{ width: '12px', height: '12px' }"
                    />
                  </button>
                </div>
              </BittsTag>
            </slot>
          </template>

          <BittsTooltip
            v-if="useOverflow && overflowCount > 0"
            :mount-to-body="true"
          >
            <BittsTag
              ref="overflowTag"
              size="x-small"
              color="neutral"
              class="bitts-filter-multiselect__overflow-tag"
            >
              +{{ overflowCount }}
            </BittsTag>

            <template #title>
              <slot name="overflowTooltip" :overflow-items="overflowItems">
                {{ overflowItems.map((i) => i.label).join(', ') }}
              </slot>
            </template>
          </BittsTooltip>
        </div>

        <div class="bitts-filter-multiselect__suffix">
          <FontAwesomeIcon
            :icon="['fak', 'chevron-down']"
            :class="{ 'rotate-180': isDropdownOpen }"
            :style="{ width: '16px', height: '16px' }"
            class="transition-transform"
          />
        </div>
      </div>

      <template #content>
        <BittsOptionList
          v-model="model"
          v-model:search-term="searchQuery"
          :options="options"
          mode="multi"
          :show-checkbox="true"
          :show-group-checkbox="showGroupCheckbox"
          :use-select-all="useSelectAll"
          :use-search="useSearch"
          :search-placeholder="searchPlaceholder"
          :use-virtual-scroll="options.length > 50"
          :aria-label="placeholder"
        >
          <template v-if="$slots.option" #default="{ option }">
            <slot name="option" :option="option" />
          </template>
          <template v-if="$slots.prefix" #prefix="{ option }">
            <slot name="prefix" :option="option" />
          </template>
          <template v-if="$slots.suffix" #suffix="{ option }">
            <slot name="suffix" :option="option" />
          </template>
        </BittsOptionList>
      </template>
    </BittsPopover>
  </div>
</template>

<script setup lang="ts" generic="TOption extends { label: string; value: any }">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useResizeObserver, watchDebounced } from '@vueuse/core';
import { computed, nextTick, ref, watch } from 'vue';

import BittsOptionList from '../BittsOptionList/BittsOptionList.vue';
import BittsPopover from '../BittsPopover/BittsPopover.vue';
import BittsTag from '../BittsTag/BittsTag.vue';
import BittsTooltip from '../BittsTooltip/BittsTooltip.vue';

type BittsPopoverInstance = InstanceType<typeof BittsPopover>;

type OptionGroup = {
  label: string;
  options: readonly TOption[];
  useBorder?: boolean;
};

const props = withDefaults(
  defineProps<{
    options: readonly (TOption | OptionGroup)[];
    placeholder?: string;
    disabled?: boolean;
    error?: boolean;
    useSearch?: boolean;
    searchPlaceholder?: string;
    useOverflow?: boolean;
    useSelectAll?: boolean;
    popoverClass?: string;
    showGroupCheckbox?: boolean;
    mountToBody?: boolean;
    matchTriggerWidth?: boolean;
  }>(),
  {
    placeholder: 'Select options',
    disabled: false,
    error: false,
    useSearch: true,
    searchPlaceholder: 'Search',
    useOverflow: false,
    useSelectAll: false,
    popoverClass: '',
    showGroupCheckbox: false,
    mountToBody: true,
    matchTriggerWidth: false,
  },
);

const emit = defineEmits<{
  change: [value: TOption['value'][]];
  'search-changed': [query: string];
}>();

defineSlots<{
  'selected-item': (props: { item: TOption; onRemove: () => void }) => unknown;
  overflowTooltip: (props: { overflowItems: TOption[] }) => unknown;
  option: (props: { option: TOption }) => unknown;
  prefix: (props: { option: TOption }) => unknown;
  suffix: (props: { option: TOption }) => unknown;
  tagPrefix: (props: { item: TOption }) => unknown;
}>();

function isOptionGroup(item: unknown): item is OptionGroup {
  return (
    typeof item === 'object' &&
    item !== null &&
    'options' in item &&
    Array.isArray((item as OptionGroup).options)
  );
}

const model = defineModel<TOption['value'][]>({ default: () => [] });

const popoverRef = ref<BittsPopoverInstance | null>(null);

function forceAlign() {
  popoverRef.value?.forceAlign();
}

const rootRef = ref<HTMLElement | null>(null);
const triggerWidth = ref(0);

useResizeObserver(rootRef, (entries) => {
  if (props.matchTriggerWidth && entries[0]) {
    triggerWidth.value = entries[0].contentRect.width;
  }
});

const overlayStyle = computed(() =>
  props.matchTriggerWidth && triggerWidth.value
    ? { width: `${triggerWidth.value}px`, maxWidth: `${triggerWidth.value}px` }
    : undefined,
);

const popoverClasses = computed(() =>
  ['bitts-filter-multiselect__popover', props.popoverClass]
    .filter(Boolean)
    .join(' '),
);

const isDropdownOpen = ref(false);
const searchQuery = ref('');

// Emit search-changed for async options fetching
watchDebounced(
  searchQuery,
  (query) => {
    emit('search-changed', query);
  },
  { debounce: 300 },
);

const selectedItemsContainer = ref<HTMLElement | null>(null);
const overflowTag = ref<HTMLElement | null>(null);
const tagRefs = ref<HTMLElement[]>([]);
const visibleCount = ref(Number.MAX_SAFE_INTEGER);

const allFlatOptions = computed<TOption[]>(() =>
  props.options.flatMap((item) =>
    isOptionGroup(item) ? [...item.options] : [item as TOption],
  ),
);

const selectedItems = computed(() =>
  (model.value || [])
    .map((val) => allFlatOptions.value.find((opt) => opt.value === val))
    .filter((opt): opt is TOption => Boolean(opt)),
);

const overflowItems = computed(() =>
  props.useOverflow ? selectedItems.value.slice(visibleCount.value) : [],
);

const overflowCount = computed(() => overflowItems.value.length);

const ariaLabel = computed(() => {
  const count = selectedItems.value.length;
  if (count === 0) return props.placeholder;
  return `${props.placeholder}, ${count} ${count === 1 ? 'item' : 'items'} selected`;
});

function setTagRef(el: unknown, index: number) {
  if (el && typeof el === 'object' && '$el' in el) {
    tagRefs.value[index] = el.$el as HTMLElement;
  }
}

async function calculateVisibleCount() {
  if (
    !props.useOverflow ||
    !selectedItemsContainer.value ||
    !tagRefs.value.length
  ) {
    visibleCount.value = Number.MAX_SAFE_INTEGER;
    return;
  }

  // ensure all tags are visible for measurement
  visibleCount.value = Number.MAX_SAFE_INTEGER;
  await nextTick();

  if (!selectedItemsContainer.value) return;

  const containerWidth =
    selectedItemsContainer.value.getBoundingClientRect().width;
  const gap =
    parseFloat(getComputedStyle(selectedItemsContainer.value).gap) || 0;

  const overflowWidth = overflowTag.value?.getBoundingClientRect().width ?? 50;

  let currentWidth = 0;
  let overflowIndex = -1;

  // pass 1: find natural overflow
  for (let i = 0; i < tagRefs.value.length; i++) {
    const tagWidth = tagRefs.value[i]?.getBoundingClientRect().width ?? 0;

    const widthToAdd = tagWidth + (i > 0 ? gap : 0);

    if (currentWidth + widthToAdd > containerWidth) {
      overflowIndex = i;
      break;
    }

    currentWidth += widthToAdd;
  }

  if (overflowIndex === -1) return;

  // pass 2: backtrack to fit +n
  for (let fit = overflowIndex; fit >= 0; fit--) {
    let width = 0;

    for (let j = 0; j < fit; j++) {
      width +=
        (tagRefs.value[j]?.getBoundingClientRect().width ?? 0) +
        (j > 0 ? gap : 0);
    }

    if (width + gap + overflowWidth <= containerWidth) {
      visibleCount.value = fit;
      return;
    }
  }

  visibleCount.value = 0;
}

watch(selectedItems, async () => {
  tagRefs.value = [];
  await nextTick();
  calculateVisibleCount();
});

useResizeObserver(selectedItemsContainer, () => {
  requestAnimationFrame(calculateVisibleCount);
});

function handleTriggerClick() {
  if (!props.disabled) {
    isDropdownOpen.value = !isDropdownOpen.value;
  }
}

function handleRemove(item: TOption) {
  if (props.disabled) return;

  const values = [...(model.value || [])];
  const index = values.indexOf(item.value);

  if (index !== -1) {
    values.splice(index, 1);
    model.value = values;
    emit('change', values);
  }
}

function getItemKey(item: TOption) {
  return String(item.value);
}

defineExpose({ forceAlign });
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";

.bitts-filter-multiselect__trigger {
  @apply relative flex items-center gap-8 px-8 py-4 border border-neutral-border-strong rounded-lg
  bg-white cursor-pointer transition-colors shadow-component min-h-32 max-h-[120px];
}

.bitts-filter-multiselect__trigger.disabled {
  @apply opacity-60 pointer-events-none;
}

.bitts-filter-multiselect__placeholder {
  @apply flex-1 text-base text-neutral-text-placeholder;
}

.bitts-filter-multiselect__selected-items {
  @apply flex-1 flex flex-wrap gap-4 -my-2 py-2;
  max-height: 88px;
  overflow-y: auto;
}

.bitts-filter-multiselect__selected-items--overflow {
  @apply flex-nowrap overflow-hidden;
  max-height: none;
}

.bitts-filter-multiselect__tag {
  @apply inline-flex items-center gap-4 shrink-0;
}

.bitts-filter-multiselect__tag--hidden {
  @apply hidden;
}

.bitts-filter-multiselect__remove-button {
  @apply relative text-neutral-text-weak hover:text-neutral-text cursor-pointer flex items-center justify-center;
}

.bitts-filter-multiselect__remove-button::before {
  content: '';
  @apply absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2;
}

.bitts-filter-multiselect__suffix {
  @apply flex items-center text-neutral-accent;
}

.bitts-filter-multiselect__overflow-tag {
  @apply inline-flex items-center shrink-0;
}

.bitts-filter-multiselect__selected-items > .bitts-tooltip {
  line-height: 0;
}

:deep(.bitts-tag .ant-tag) {
  @apply m-0;
}
</style>
